import React, { useRef, useEffect, useMemo, useState } from 'react';
import * as d3 from 'd3';
import { useTheme } from '../../contexts';
import { deriveEventCategory, type EventCategory } from '../../constants/eventCategories';
import { renderTimeline, type TimelineItem, type TimelineRow, type DataMode } from '../timeline/timelineRenderer';
import type { SiteEvent } from '../../redux/api/eventsApi';
import type { SiteTask } from '../../redux/api/tasksApi';

interface EventsTimelineStripProps {
  events: SiteEvent[];
  tasks: SiteTask[];
  dataMode: DataMode;
  taskEventDateMap: Map<string, string>;
  onEventClick?: (eventId: string) => void;
  height?: number;
}

/**
 * Single-row D3 timeline strip for the Events/Tasks tab header.
 * Uses the same timelineRenderer as the full InteractiveTimelineTab, in mini mode.
 */
const EventsTimelineStrip: React.FC<EventsTimelineStripProps> = ({
  events,
  tasks,
  dataMode,
  taskEventDateMap,
  onEventClick,
  height = 64,
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const rows = useMemo((): TimelineRow[] => {
    const items: TimelineItem[] = [];

    if (dataMode === 'events') {
      events.forEach((event) => {
        const category = deriveEventCategory(event.event_type, event.occurred_at);
        items.push({
          id: event.id,
          date: new Date(event.occurred_at),
          category,
          severity: event.severity,
          title: event.title,
          status: event.status,
          isTask: false,
          eventType: event.event_type,
          entityIdentifiers: (event as any).affected_entities?.map((e: any) => e.entity_identifier) || [],
        });
      });
    } else {
      tasks.forEach((task) => {
        const eventDate = taskEventDateMap.get(task.id);
        const date = eventDate ? new Date(eventDate) : new Date(task.created_at);
        items.push({
          id: task.id,
          date,
          category: 'corrective_maintenance' as EventCategory,
          severity: task.priority ? (task.priority <= 2 ? 'critical' : task.priority <= 4 ? 'high' : task.priority <= 6 ? 'medium' : 'low') : 'medium',
          priority: task.priority,
          title: task.title,
          status: task.status,
          isTask: true,
        });
      });
    }

    if (items.length === 0) return [];

    return [{
      id: 'all',
      label: dataMode === 'events' ? 'Events' : 'Tasks',
      items,
      isGroup: false,
    }];
  }, [events, tasks, dataMode, taskEventDateMap]);

  const timelineWidth = useMemo(() => {
    const items = rows[0]?.items ?? [];
    if (items.length === 0 || containerWidth <= 0) return containerWidth;
    const extent = d3.extent(items, (d) => d.date) as [Date, Date];
    if (!extent[0] || !extent[1]) return containerWidth;
    const days = Math.max(1, (extent[1].getTime() - extent[0].getTime()) / 86_400_000);
    return Math.max(containerWidth, 16 + days * 10);
  }, [rows, containerWidth]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setContainerWidth(entry.contentRect.width);
    });
    observer.observe(containerRef.current);
    setContainerWidth(containerRef.current.getBoundingClientRect().width);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current || rows.length === 0 || timelineWidth <= 0) return;

    const { cleanup } = renderTimeline(svgRef.current, {
      mode: 'mini',
      width: timelineWidth,
      height,
      rows,
      dataMode,
      highlightTopN: 15,
      isDark,
      callbacks: {
        onItemClick: (item) => {
          if (!item.isTask && onEventClick) {
            onEventClick(item.id);
          }
        },
      },
    });

    return cleanup;
  }, [rows, height, isDark, dataMode, onEventClick, timelineWidth]);

  // Re-render on resize
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(() => {
      if (!svgRef.current || !containerRef.current || rows.length === 0 || timelineWidth <= 0) return;

      renderTimeline(svgRef.current, {
        mode: 'mini',
        width: timelineWidth,
        height,
        rows,
        dataMode,
        highlightTopN: 15,
        isDark,
        callbacks: {
          onItemClick: (item) => {
            if (!item.isTask && onEventClick) {
              onEventClick(item.id);
            }
          },
        },
      });
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [rows, height, isDark, dataMode, onEventClick, timelineWidth]);

  if (rows.length === 0 || (events.length === 0 && tasks.length === 0)) {
    return null;
  }

  return (
    <div ref={containerRef} className="w-full overflow-x-auto">
      <svg ref={svgRef} width={timelineWidth || '100%'} height={height} className="block min-w-full" />
    </div>
  );
};

export default EventsTimelineStrip;
